import React, { useState, useEffect, useRef } from 'react';
import { useAdmin } from './AdminContext';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Search, Truck, Edit, Ban, RotateCcw, Upload, Download, AlertTriangle, Clock, CheckCircle2, FileText } from 'lucide-react';

const EMPTY = { company: '', vendor_type: 'Carrier', contact_name: '', phone: '', email: '', remit_to_address: '', payment_terms: 'Net 30', notes: '', coi_expiry_date: '' };
const TYPES = ['Carrier', 'Staffing Vendor', 'Both'];
const TERMS = ['Net 15', 'Net 30', 'Net 45', 'Net 60'];

const COI_BADGE = {
  expired: { icon: AlertTriangle, label: 'COI Expired', color: '#DC2626', bg: '#DC262615' },
  expiring_soon: { icon: Clock, label: 'Expiring Soon', color: '#F59E0B', bg: '#F59E0B15' },
  valid: { icon: CheckCircle2, label: 'COI Valid', color: '#10B981', bg: '#10B98115' },
  none: { icon: FileText, label: 'No COI', color: '#9CA3AF', bg: '#9CA3AF15' },
};

export default function AdminVendors() {
  const { authFetch, canWrite, isAdmin, token } = useAdmin();
  const [params] = useSearchParams();
  const [vendors, setVendors] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [typeFilter, setTypeFilter] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [uploadModal, setUploadModal] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadExpiry, setUploadExpiry] = useState('');
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const q = new URLSearchParams();
      if (statusFilter) q.set('status', statusFilter);
      if (search) q.set('search', search);
      if (typeFilter) q.set('vendor_type', typeFilter);
      const res = await authFetch(`/api/admin/vendors?${q}`);
      if (res.ok) setVendors(await res.json());
    } catch {}
  };

  useEffect(() => { load(); }, [statusFilter, typeFilter]);
  useEffect(() => { if (params.get('action') === 'new' && canWrite()) openNew(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setModalOpen(true); };
  const openEdit = (v) => {
    setEditing(v);
    setForm({ company: v.company, vendor_type: v.vendor_type, contact_name: v.contact_name, phone: v.phone, email: v.email, remit_to_address: v.remit_to_address, payment_terms: v.payment_terms, notes: v.notes, coi_expiry_date: v.coi_expiry_date || '' });
    setModalOpen(true);
  };

  const save = async () => {
    if (!form.company.trim()) { toast.error('Company name required'); return; }
    setSaving(true);
    try {
      const method = editing ? 'PUT' : 'POST';
      const url = editing ? `/api/admin/vendors/${editing.id}` : '/api/admin/vendors';
      const res = await authFetch(url, { method, body: JSON.stringify(form) });
      if (res.ok) { toast.success(editing ? 'Vendor updated' : 'Vendor created'); setModalOpen(false); load(); }
      else { const e = await res.json(); toast.error(e.detail || 'Error'); }
    } catch (err) { toast.error(err.message); } finally { setSaving(false); }
  };

  const toggleStatus = async (v) => {
    const newStatus = v.status === 'active' ? 'inactive' : 'active';
    try {
      const res = await authFetch(`/api/admin/vendors/${v.id}`, { method: 'PUT', body: JSON.stringify({ status: newStatus }) });
      if (res.ok) { toast.success(`Vendor ${newStatus === 'active' ? 'reactivated' : 'deactivated'}`); load(); }
    } catch {}
  };

  const handleUpload = async () => {
    if (!fileRef.current?.files?.[0]) { toast.error('Select a file'); return; }
    if (uploadModal.type === 'coi' && !uploadExpiry) { toast.error('COI expiry date required'); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append('file', fileRef.current.files[0]);
    if (uploadModal.type === 'coi') fd.append('expiry_date', uploadExpiry);
    try {
      const t = token || localStorage.getItem('admin_token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/vendors/${uploadModal.vendor.id}/${uploadModal.type}`, {
        method: 'POST', headers: { Authorization: `Bearer ${t}` }, body: fd
      });
      if (res.ok) { toast.success(`${uploadModal.type.toUpperCase()} uploaded`); setUploadModal(null); setUploadExpiry(''); load(); }
      else { const e = await res.json(); toast.error(e.detail || 'Upload failed'); }
    } catch (err) { toast.error(err.message); } finally { setUploading(false); }
  };

  const downloadFile = (vendorId, type) => {
    const t = token || localStorage.getItem('admin_token');
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/admin/vendors/${vendorId}/${type}?token=${t}`, '_blank');
  };

  const CoiBadge = ({ status }) => {
    const cfg = COI_BADGE[status] || COI_BADGE.none;
    const Icon = cfg.icon;
    return <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full" style={{ background: cfg.bg, color: cfg.color }}><Icon className="w-3 h-3" />{cfg.label}</span>;
  };

  return (
    <div data-testid="admin-vendors">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
        <h1 className="text-2xl font-bold" style={{ color: '#1B2A6B', fontFamily: 'Chivo, sans-serif' }}>Vendors</h1>
        {canWrite() && <Button onClick={openNew} className="gap-2 font-semibold" style={{ background: '#B8962E', color: '#fff' }} data-testid="add-vendor-btn"><Plus className="w-4 h-4" /> Add Vendor</Button>}
      </div>

      <Card className="border border-gray-200 mb-6"><CardContent className="p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && load()} placeholder="Search vendors..." className="pl-10" data-testid="vendor-search" />
          </div>
          <div className="flex gap-2 flex-wrap">
            {['active', 'inactive', ''].map(s => (
              <Button key={s} variant={statusFilter === s ? 'default' : 'outline'} size="sm" onClick={() => setStatusFilter(s)} style={statusFilter === s ? { background: '#1B2A6B', color: '#fff' } : {}}>{s === '' ? 'All' : s === 'active' ? 'Active' : 'Inactive'}</Button>
            ))}
            <Select value={typeFilter} onValueChange={v => setTypeFilter(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-40" data-testid="type-filter"><SelectValue placeholder="All Types" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardContent></Card>

      <div className="space-y-3">
        {vendors.length === 0 && <p className="text-center text-gray-400 py-12">No vendors found</p>}
        {vendors.map(v => (
          <Card key={v.id} className="border border-gray-200 hover:shadow-sm transition-shadow" data-testid={`vendor-${v.id}`}>
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: '#B8962E15' }}>
                    <Truck className="w-5 h-5" style={{ color: '#B8962E' }} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold" style={{ color: '#1B2A6B' }}>{v.company}</span>
                      <Badge variant="outline" className="text-xs">{v.vendor_type}</Badge>
                      <Badge variant={v.status === 'active' ? 'default' : 'secondary'} className="text-xs" style={v.status === 'active' ? { background: '#10B98122', color: '#10B981' } : {}}>{v.status}</Badge>
                      <CoiBadge status={v.coi_status} />
                    </div>
                    <p className="text-sm text-gray-500">{v.contact_name}{v.phone ? ` • ${v.phone}` : ''}{v.email ? ` • ${v.email}` : ''}</p>
                    <p className="text-xs text-gray-400 mt-1">Terms: {v.payment_terms}{v.coi_expiry_date ? ` • COI Exp: ${v.coi_expiry_date}` : ''}</p>
                  </div>
                </div>
                <div className="flex gap-2 shrink-0 flex-wrap">
                  {canWrite() && (
                    <>
                      <Button variant="outline" size="sm" onClick={() => setUploadModal({ vendor: v, type: 'w9' })} className="gap-1 text-xs" data-testid={`upload-w9-${v.id}`}><Upload className="w-3 h-3" /> W-9</Button>
                      <Button variant="outline" size="sm" onClick={() => setUploadModal({ vendor: v, type: 'coi' })} className="gap-1 text-xs" data-testid={`upload-coi-${v.id}`}><Upload className="w-3 h-3" /> COI</Button>
                    </>
                  )}
                  {v.w9_filename && <Button variant="outline" size="sm" onClick={() => downloadFile(v.id, 'w9')} className="gap-1 text-xs"><Download className="w-3 h-3" /> W-9</Button>}
                  {v.coi_filename && <Button variant="outline" size="sm" onClick={() => downloadFile(v.id, 'coi')} className="gap-1 text-xs"><Download className="w-3 h-3" /> COI</Button>}
                  {canWrite() && <Button variant="outline" size="sm" onClick={() => openEdit(v)} className="gap-1"><Edit className="w-3.5 h-3.5" /> Edit</Button>}
                  {isAdmin() && (
                    <Button variant="outline" size="sm" onClick={() => toggleStatus(v)} className={`gap-1 ${v.status === 'active' ? 'text-red-600 border-red-200 hover:bg-red-50' : 'text-green-600 border-green-200 hover:bg-green-50'}`}>
                      {v.status === 'active' ? <><Ban className="w-3.5 h-3.5" /> Deactivate</> : <><RotateCcw className="w-3.5 h-3.5" /> Reactivate</>}
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Create/Edit Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-lg" data-testid="vendor-modal">
          <DialogHeader><DialogTitle style={{ color: '#1B2A6B', fontFamily: 'Chivo, sans-serif' }}>{editing ? 'Edit Vendor' : 'New Vendor'}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label className="text-sm font-semibold">Company *</Label><Input value={form.company} onChange={e => setForm({...form, company: e.target.value})} data-testid="form-company" /></div>
              <div className="space-y-1.5"><Label className="text-sm font-semibold">Type *</Label>
                <Select value={form.vendor_type} onValueChange={v => setForm({...form, vendor_type: v})}>
                  <SelectTrigger data-testid="form-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label className="text-sm font-semibold">Contact Name</Label><Input value={form.contact_name} onChange={e => setForm({...form, contact_name: e.target.value})} data-testid="form-contact" /></div>
              <div className="space-y-1.5"><Label className="text-sm font-semibold">Phone</Label><Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} data-testid="form-phone" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label className="text-sm font-semibold">Email</Label><Input value={form.email} onChange={e => setForm({...form, email: e.target.value})} data-testid="form-email" /></div>
              <div className="space-y-1.5"><Label className="text-sm font-semibold">Payment Terms</Label>
                <Select value={form.payment_terms} onValueChange={v => setForm({...form, payment_terms: v})}>
                  <SelectTrigger data-testid="form-terms"><SelectValue /></SelectTrigger>
                  <SelectContent>{TERMS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5"><Label className="text-sm font-semibold">Remit-To Address</Label><Textarea value={form.remit_to_address} onChange={e => setForm({...form, remit_to_address: e.target.value})} rows={2} data-testid="form-remit" /></div>
            <div className="space-y-1.5"><Label className="text-sm font-semibold">Notes</Label><Textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={2} data-testid="form-notes" /></div>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setModalOpen(false)}>Cancel</Button>
              <Button onClick={save} disabled={saving} style={{ background: '#B8962E', color: '#fff' }} data-testid="form-save">{saving ? 'Saving...' : editing ? 'Update' : 'Create'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Upload Modal */}
      <Dialog open={!!uploadModal} onOpenChange={() => { setUploadModal(null); setUploadExpiry(''); }}>
        <DialogContent className="max-w-sm" data-testid="upload-modal">
          <DialogHeader><DialogTitle style={{ color: '#1B2A6B' }}>Upload {uploadModal?.type === 'w9' ? 'W-9' : 'COI'} — {uploadModal?.vendor?.company}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-1.5">
              <Label className="text-sm font-semibold">Select File</Label>
              <Input type="file" ref={fileRef} accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" data-testid="file-input" />
            </div>
            {uploadModal?.type === 'coi' && (
              <div className="space-y-1.5">
                <Label className="text-sm font-semibold">COI Expiry Date *</Label>
                <Input type="date" value={uploadExpiry} onChange={e => setUploadExpiry(e.target.value)} data-testid="coi-expiry-input" />
              </div>
            )}
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setUploadModal(null)}>Cancel</Button>
              <Button onClick={handleUpload} disabled={uploading} style={{ background: '#B8962E', color: '#fff' }} data-testid="upload-submit">{uploading ? 'Uploading...' : 'Upload'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
